import {observer_mgl} from "mobx-graphlink";
import React, {useEffect, useState} from "react";
import {CheckBox, Column, Row, Spinner, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, InfoButton, RunInAction_Set, TextPlus} from "web-vcore";
import {DMap} from "../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../Store/main/maps/@MapState.js";
import {mapRenderStats} from "../../../Utils/ThreeJS/MapObjectRenderStats.js";
import {invalidate3JSStats} from "../../../Utils/ThreeJS/Invalidate3JS.js";
import {terrainProcessStats} from "../../../Terrain/Heights/TerrainProcessStats.js";
import {terrainBaseRecipeID, terrainCommittedChunkInfoAtPoint, terrainMaterialRegions, terrainQuadtree_current} from "../../../Terrain/Heights/TerrainRenderer.js";
import {sceneryRenderStats} from "../../../Terrain/Scenery/Render/SceneryRenderStats.js";
import {buildingRenderStats} from "../../../Building/BuildingRenderStats.js";
import {MAX_CHUNKS_HARD_CEILING} from "../../../Terrain/Heights/TerrainQuadtree.js";
import {recipesAtPoint, slopeFromNormal} from "../../../Terrain/Materials/Splat/SplatRasterizer.js";
import {GetProcMatRecipes} from "../../../Server/DB/mapObjects/ProcMatAccessors.js";

export const StatsUI = observer_mgl(({map, mapState}: {map: DMap, mapState: MapState})=>{
	const info = mapRenderStats.rendererInfo;
	const {frameRenderMs, totalFrameMs} = mapRenderStats;
	const overheadMs = frameRenderMs != null && totalFrameMs != null ? totalFrameMs - frameRenderMs : null;
	const fmt = (ms: number | null)=>{
		return ms != null ? `${ms.toFixed(1)} ms` : "— ms";
	};
	const fmtFpsNum = (ms: number | null)=>ms != null && ms > 0 ? (1000 / ms).toFixed(1) : "—";
	const pad = (a: string, b: string): [string, string]=>{
		const diff = a.length - b.length;
		if (diff > 0) return [a, " ".repeat(diff) + b];
		if (diff < 0) return [" ".repeat(-diff) + a, b];
		return [a, b];
	};
	const [render_timeMsg, overhead_timeMsg] = pad(fmt(frameRenderMs), fmt(overheadMs));
	const [render_fps, overhead_fps] = pad(fmtFpsNum(frameRenderMs), fmtFpsNum(totalFrameMs));
	const render_fpsMsg = `(fps: ${render_fps})`;
	const overhead_fpsMsg = `(fps: ${overhead_fps} final)`;

	const sortedObjs = [...mapRenderStats.objStats.values()]
		.sort((a, b)=>b.elapsedMs - a.elapsedMs)
		.slice(0, mapState.stats_objRowsToShow);

	const cellStyle = (flex: number, right = false): React.CSSProperties=>({
		flex, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
		...(right ? {textAlign: "right"} : {}),
	});

	const monoAndPre = {fontFamily: "monospace", whiteSpace: "pre"} as const;
	return (
		<ScrollView className="selectable" style={{flex: 1, padding: 5, overflow: "hidden"}}>
			<Row>
				<CheckBox text="Force continuous rendering" value={mapState.settings.general.forceContinuousRendering}
					onChange={val=>RunInAction_Set(()=>mapState.settings.general.forceContinuousRendering = val)}/>
				<InfoButton ml={5} text={`
					When enabled, the map will render continuously, instead of only when changes occur. Useful for:
					* Getting an accurate "Overhead time" measurement.
					* In case re-renders are failing to be auto-triggered for certain types of changes.
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Preserve drawing buffer (debug)" value={mapState.settings.general.debug_preserveDrawingBuffer}
					onChange={val=>RunInAction_Set(()=>mapState.settings.general.debug_preserveDrawingBuffer = val)}/>
				<InfoButton ml={5} text={`
					Debug aid: keeps the WebGL drawing buffer after each frame, so the agent/devtools can read actual rendered pixels via readPixels (instead of getting all-zeroes). Perf-neutral to leave off in production.
				`.AsMultiline(0)}/>
			</Row>

			{/* Terrain Info — wall-clock timings for the terrain pipeline's solve + eval phases. */}
			<TerrainInfoSection map={map} mapState={mapState}/>

			{/* Scenery Info — instanced + billboarded tree counts (Set15.5). */}
			<Row mt={5} mb={3} style={{fontWeight: "bold"}}>
				<Text>Scenery</Text>
				<InfoButton ml={5} text={`
					Instanced trees = the near-tier REAL meshes (per-variant InstancedMesh, real geometry; 3D only).
					Billboarded trees = the far-tier cylindrical billboards (3D) or the flat tree quads (2D).
					In 2D every tree is billboarded and instanced is always 0. Zero of both ⇒ no scenery is being drawn.
				`.AsMultiline(0)}/>
			</Row>
			{sceneryRenderStats.instancedTreeCount == null && <Row style={{color: "gray", fontSize: 12}}>No scenery data yet…</Row>}
			{sceneryRenderStats.instancedTreeCount != null &&
			<Row style={{fontSize: 12, gap: 6}}>
				<Text>Instanced: </Text><Text style={{fontWeight: "bold"}}>{sceneryRenderStats.instancedTreeCount.toLocaleString()}</Text>
				<Text ml={10}>Billboarded: </Text><Text style={{fontWeight: "bold"}}>{(sceneryRenderStats.billboardedTreeCount ?? 0).toLocaleString()}</Text>
				<Text ml={10} style={{color: "rgba(255,255,255,0.6)"}}>({sceneryRenderStats.kindCount} kind{sceneryRenderStats.kindCount == 1 ? "" : "s"})</Text>
			</Row>}

			{/* Building Info — per-building + aggregate mesh counts (Buildings Set1 §8). Lets us
			    answer "are any Building meshes actually being produced?" at a glance. */}
			<BuildingInfoSection/>

			{/* Renderer info */}
			<Row mt={5} mb={3} style={{fontWeight: "bold"}}>
				<Text>Renderer Info</Text>
				<InfoButton ml={5} text={`
					Note: The overhead-time measurement is only accurate when:
					* Continuous-rendering is enabled (which makes it *try* to match your monitor's refresh-rate), AND...
					* The final/with-overhead FPS is *clearly below* your monitor's refresh-rate (usually 60fps).
					Reason: Overhead-time is calculated simply as [time between frames] - [actual render time for a frame].
				`.AsMultiline(0)}/>
			</Row>
			{info == null && <Row style={{color: "gray", fontSize: 12}}>Waiting for frame data…</Row>}
			{info &&
			<Column style={{fontSize: 12, gap: 1}}>
				<Row style={{alignItems: "baseline"}}>
					<Text style={{width: 100}}>Render time:</Text>
					<Text style={{...monoAndPre, fontWeight: "bold"}}>{render_timeMsg}</Text>
					<Text ml={6} style={{...monoAndPre, color: "rgba(255,255,255,0.6)"}}>{render_fpsMsg}</Text>
				</Row>
				<Row style={{alignItems: "baseline"}}>
					<Text title={mapState.settings.general.forceContinuousRendering ? null : "Values wrong unless continuous-rendering is enabled."}
						style={ES(
							{width: 100},
							!mapState.settings.general.forceContinuousRendering && {textDecoration: "line-through"},
						)}
						>Overhead time:</Text>
					<Text style={{...monoAndPre, fontWeight: "bold"}}>{overhead_timeMsg}</Text>
					<Text ml={6} style={{...monoAndPre, color: "rgba(255,255,255,0.6)"}}>{overhead_fpsMsg}</Text>
				</Row>
				<Row>Draw calls: <Text ml={4} style={{fontWeight: "bold"}}>{info.calls}</Text>
					<Text ml={10}>Triangles: </Text><Text ml={4} style={{fontWeight: "bold"}}>{info.triangles.toLocaleString()}</Text>
				</Row>
				<Row>Points: <Text ml={4}>{info.points}</Text>
					<Text ml={10}>Lines: </Text><Text ml={4}>{info.lines}</Text>
				</Row>
				<Row>Geometries: <Text ml={4}>{info.geometries}</Text>
					<Text ml={10}>Textures: </Text><Text ml={4}>{info.textures}</Text>
					<Text ml={10}>Programs: </Text><Text ml={4}>{info.programs}</Text>
				</Row>
				<Row>
					<TextPlus mr={5} info={`
						Last-invalidate reason — the "why is the map rendering?" diagnostic. Every frame request
						routes through Invalidate3JS (the single sanctioned invalidate wrapper), which records the
						source + reason here. When on-demand mode won't idle, this shows which subsystem is churning.
					`.AsMultiline(0)}>Last frame req:</TextPlus>
					{invalidate3JSStats.lastInvalidate == null
						? <Text style={{...monoAndPre, color: "gray"}}>—</Text>
						: <Text title={`${invalidate3JSStats.lastInvalidate.reason}\n\n${invalidate3JSStats.lastInvalidate.stack}`} style={{...monoAndPre, fontSize: 11}}>
							{`${invalidate3JSStats.lastInvalidate.source} (x${invalidate3JSStats.lastInvalidate.count})`}
							<Text ml={4} style={{color: "rgba(255,255,255,0.5)"}}>{"·"} total {invalidate3JSStats.totalCalls}</Text>
						</Text>}
				</Row>
			</Column>}

			{/* Per-object timing */}
			<Row mt={10} style={{alignItems: "center"}}>
				<Text style={{fontWeight: "bold"}}>Top</Text>
				<Spinner ml={5} style={{width: 55}} step={1}
					value={mapState.stats_objRowsToShow} onChange={val=>RunInAction_Set(()=>mapState.stats_objRowsToShow = val)}/>
				<Text ml={5} style={{fontWeight: "bold"}}>objects by CPU render time</Text>
			</Row>
			<Row mt={4} style={{fontSize: 11, fontWeight: "bold", borderBottom: "1px solid rgba(255,255,255,0.35)", paddingBottom: 2}}>
				<Text style={cellStyle(4)}>Name</Text>
				<Text style={cellStyle(1, true)}>ms</Text>
				<Text style={cellStyle(1, true)}>draws</Text>
			</Row>
				{sortedObjs.length == 0 &&
				<Text style={{color: "gray", fontSize: 12, marginTop: 6}}>
					No timing data yet — data updates every ~30 frames.
				</Text>}
				{sortedObjs.map((obj, i)=>(
					<Row key={i} style={{
						fontSize: 11, padding: "2px 0",
						borderBottom: "1px solid rgba(255,255,255,0.1)",
						background: i % 2 == 0 ? "transparent" : "rgba(255,255,255,0.03)",
					}}>
						<Text style={cellStyle(4)}>{obj.name}</Text>
						<Text style={cellStyle(1, true)}>{obj.elapsedMs.toFixed(3)}</Text>
						<Text style={cellStyle(1, true)}>{obj.drawCallCount}</Text>
					</Row>
				))}
		</ScrollView>
	);
});
const TerrainInfoSection = observer_mgl(({map, mapState}: {map: DMap, mapState: MapState})=>{
	// Re-render every ~250ms while a solve or eval-fill is in flight so the live
	// "in flight" elapsed counter ticks.  When nothing is pending, the interval is
	// inert (the mobx observers fire on the observable field changes).
	const [, setTick] = useState(0);
	useEffect(()=>{
		const id = setInterval(()=>setTick(t=>t + 1), 250);
		return ()=>clearInterval(id);
	}, []);

	const now = performance.now();
	const solveInFlight = terrainProcessStats.lastSolveDispatchedAt != null;
	const solveMs = solveInFlight ? now - (terrainProcessStats.lastSolveDispatchedAt as number) : terrainProcessStats.lastSolveMs;
	const evalInFlight = terrainProcessStats.lastEvalFillAt != null;
	const evalMs = evalInFlight ? now - (terrainProcessStats.lastEvalFillAt as number) : terrainProcessStats.lastEvalFillMs;
	const fmtMs = (ms: number | null, inFlight: boolean): string=>{
		if (ms == null) return "—";
		const inFlightSuffix = inFlight ? " (in flight…)" : "";
		//if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s${inFlightSuffix}`;
		return `${ms.toFixed(0)} ms${inFlightSuffix}`;
	};

	const fmtMeters = (m: number | null): string=>{
		if (m == null) return "—";
		return `${m.toFixed(1)} m`;
	};

	// Resolve the procedural-material recipe(s) present at the mouse's current world position.
	// The mouse world pos is tracked on `mapState` (see MapControls), sampled alongside the same
	// elevation/slope inputs the splat rasteriser uses (from the finest evaluated leaf chunk), and
	// ranked through the identical top-K logic so the readout matches what the material system
	// actually renders there (slot 0 = the map's base recipe, then the winning region, if any).
	const materialsAtMouse = (map_: DMap): string=>{
		const mousePos = mapState.mousePos_world_0ms;
		if (mousePos == null) return "—";
		const qt = terrainQuadtree_current;
		if (qt == null) return "—";
		const elevation = qt.sampleHeightAt(mousePos.x, mousePos.y);
		const normal = qt.sampleNormalAt(mousePos.x, mousePos.y);
		if (elevation == null || normal == null) return "—";
		const slope = slopeFromNormal(normal.x, normal.y, normal.z);
		const baseRecipeID = terrainBaseRecipeID(map_.id);
		const ids = recipesAtPoint(mousePos.x, mousePos.y, elevation, slope, terrainMaterialRegions, baseRecipeID);
		// Map each recipe id to its human name (falling back to the raw id if unresolvable).
		const recipesByID = GetProcMatRecipes(map_.id).ToMap(a=>a.id, a=>a);
		return ids.map(id=>{
			const r = recipesByID.get(id);
			return r ? r.name : id;
		}).join(" + ");
	};

	// Resolve the procedural-material recipe(s) ACTUALLY committed to the chunk that covers the
	// mouse's current world position — read from the renderer's published per-chunk splat map (the
	// live rasterised slot order, slot 0 = the map's base recipe), NOT re-derived from the region
	// set.  This is the ground-truth readout: it reflects exactly which materials that chunk's
	// committed splat data uses (what the shader actually samples).
	const chunkMaterialsAtMouse = (map_: DMap): string=>{
		const mousePos = mapState.mousePos_world_0ms;
		if (mousePos == null) return "—";
		const info = terrainCommittedChunkInfoAtPoint(mousePos.x, mousePos.y);
		if (info == null) return "—";
		// Map each recipe id to its human name (falling back to the raw id if unresolvable).
		const recipesByID = GetProcMatRecipes(map_.id).ToMap(a=>a.id, a=>a);
		const names = info.recipeIDs.map(id=>{
			const r = recipesByID.get(id);
			return r ? r.name : id;
		}).join(" + ");
		// Append the chunk's grid X/Y index (eg. "Grass + Grass_Jungle [25,30]").
		return `[${info.indexX},${info.indexY}] ${names}`;
	};

	const monoAndPre = {fontFamily: "monospace", whiteSpace: "pre"} as const;
	return (
		<Column mt={10} mb={3} style={{fontSize: 12, gap: 1}}>
			<Row mb={3} style={{fontWeight: "bold", alignItems: "center"}}>
				<Text>Terrain Info</Text>
				<InfoButton ml={5} text={`
					Wall-clock timings for the terrain pipeline's two main phases, captured by TerrainRenderer:
					* Solve: the worker-0 solve round-trip — \`prepareTerrain\` (macro + working-grid build, on load / config change) OR the standalone \`solveTerrain\` (working-grid refresh on camera move), whichever last settled.  Both end at the same reply-observed settlement.
					* Eval/Sample chunks: the full "camera-fill" eval phase — from the first \`evalChunkBatch\` dispatch after a solve settles until the quadtree's eval queue for the current view is drained (all view chunks processed).
					Both are wall-clock (include worker round-trip + any queue/hop latency).  A pending phase shows a live "in flight…" elapsed.
				`.AsMultiline(0)}/>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Solve:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{fmtMs(solveMs, solveInFlight)}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Eval/Sample:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{fmtMs(evalMs, evalInFlight)}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Total:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{fmtMs(solveMs && evalMs ? solveMs + evalMs : null, solveInFlight || evalInFlight)}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Fine-cell size:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{[
					fmtMeters(terrainProcessStats.fineCellSize_final),
					`(requested: ${fmtMeters(terrainProcessStats.fineCellSize_requested)})`,
					`(mode: ${terrainProcessStats.isPerspective ? "3D" : "2D"})`,
				].join(" ")}
				</Text>
			</Row>
			{/* Set15.5 P5 (§7) — render-lattice diagnostics.  The set's raison d'être: the render
			    lattice was 64× too coarse while every solve stat read healthy.  `renderLatticeSpacing`
			    vs `fineCellSize_final` is the direct lattice↔solve comparison — a large ratio IS the
			    §1.1 bug.  `lodCoarseningFactor` ≠ 1 ⇒ the chunk budget is binding (quality traded);
			    `chunkCount` pinned at the ceiling while the factor is > 1 ⇒ the tree is saturated. */}
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Render lattice:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{[
					fmtMeters(terrainProcessStats.renderLatticeSpacing_atViewCentre),
					`(fine: ${fmtMeters(terrainProcessStats.fineCellSize_final)})`,
				].join(" ")}
				</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Cell leaves:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{
					`${terrainProcessStats.visibleLeafCount ?? "—"} / ${terrainProcessStats.demandedLeafCount ?? "—"}`
				}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>LOD factor:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{
					terrainProcessStats.lodCoarseningFactor != null ? terrainProcessStats.lodCoarseningFactor.toFixed(3) : "—"
				}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Chunks:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{
					`${terrainProcessStats.chunkCount ?? "—"} / ${MAX_CHUNKS_HARD_CEILING}`
				}</Text>
			</Row>
			{/* Set9 follow-up — the per-set shader-program count: how many DISTINCT terrain shaders
			    this map's region layout generated.  Structurally-identical recipe sets collapse onto
			    one program (D1), so this is the number of genuinely-distinct structural-hash sets +
			    the base-less fast-path program.  A count that grows with every chunk/zoom ⇒ the
			    region layout is producing many per-chunk material combinations (the Set9 narrow-phase
			    follow-up concern); a small count ⇒ few compiled shaders serve the whole map. */}
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Terrain programs:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{terrainProcessStats.terrainProgramCount ?? "—"}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Mats at point:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{materialsAtMouse(map)}</Text>
			</Row>
			{/* Set12 — the CHUNK-COMMITTED variant of the Materials row: rather than re-deriving the
			    material set at the point from the region set (the "similar call-path" of
			    `materialsAtMouse` above), this reads the recipe ids the covering chunk's splat was
			    ACTUALLY rasterised with (the renderer's published per-chunk splat map — ground
			    truth for what the shader samples).  `—` when no committed chunk splat covers the
			    point (terrain off / chunk not yet rasterised / splats disabled). */}
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100, minWidth: 100}}>Mats in chunk:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold", whiteSpace: "pre-wrap"}}>{chunkMaterialsAtMouse(map)}</Text>
			</Row>
			{/* Eval-pipeline diagnostics — surface WHY the "Eval/Sample" stat is stuck "in flight…".
			    `evalQueueLength`>0 (unevaluated leaves still queued) vs `evalInFlight`>0 (a dispatch
			    awaiting a worker reply) are the two conditions that keep the eval-fill window open.
			    The dispatch/reply counters show whether batches are settling (equal = settled). */}
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Eval queue:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{[
					`q:${terrainProcessStats.evalQueueLength ?? "—"}`,
					`inflight:${terrainProcessStats.evalInFlight ?? "—"}`,
					`halted:${terrainProcessStats.evalHaltedAtCeiling ?? "—"}`,
				].join("  ")}
				</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Eval batches:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{
					`sent:${terrainProcessStats.evalDispatchesSent}  replied:${terrainProcessStats.evalRepliesApplied}`
				}</Text>
			</Row>
			<Row style={{alignItems: "baseline"}}>
				<Text style={{width: 100}}>Worker ready:</Text>
				<Text style={{...monoAndPre, fontWeight: "bold"}}>{
					`[${(terrainProcessStats.workerReadyGens ?? []).join(",")}]  gen:${terrainProcessStats.cameraGen ?? "—"}`
				}</Text>
			</Row>
			{/* Set15.5 P6 — the 3D hard-ceiling HALT warning.  In 3D, the LOD budget no longer
			    coarsens to fit; instead the tree evaluates up to `MAX_CHUNKS_HARD_CEILING` and then
			    HALTS, leaving the far field uneval'd.  This tells the user their viewpoint/viewer
			    settings demand more chunks than the hard limit allows, and how to resolve it. */}
			{terrainProcessStats.renderLimitHit === true && (
				<Row mt={5} p={6} style={{
					background: "rgba(200,40,40,0.25)", border: "1px solid rgba(255,80,80,0.6)",
					borderRadius: 4, alignItems: "flex-start", gap: 6,
				}}>
					<Text style={{color: "#ff8080", fontWeight: "bold", fontSize: 12}}>⚠</Text>
					<Column style={{gap: 2}}>
						<Text style={{color: "#ff9090", fontWeight: "bold", fontSize: 11}}>
							Chunk limit hit — far terrain not rendered.
						</Text>
						<Text style={{color: "#ffb0b0", fontSize: 10, lineHeight: 1.4}}>
							This viewpoint + viewer settings demand more than {MAX_CHUNKS_HARD_CEILING.toLocaleString()} terrain chunks (the hard limit), so the farthest terrain is left unevaluated.
							{"\n"}Fix: lower the "max px/sample" setting, lower "max samples/fine-cell-edge", reduce the view distance, or move the viewpoint closer to the terrain.
						</Text>
					</Column>
				</Row>
			)}
		</Column>
	);
});
const BuildingInfoSection = observer_mgl(()=>{
	const vals = [...buildingRenderStats.buildings.values()];
	const buildingCount = vals.length;
	const meshCount = vals.reduce((acc, v)=>acc + v.meshCount, 0);
	const roofCount = vals.reduce((acc, v)=>acc + v.roofCount, 0);
	const floorCount = vals.reduce((acc, v)=>acc + v.floorCount, 0);
	const roomCount = vals.reduce((acc, v)=>acc + v.roomCount, 0);
	const geometryKeyCount = vals.reduce((acc, v)=>acc + v.geometryKeyCount, 0);
	const monoAndPre = {fontFamily: "monospace", whiteSpace: "pre"} as const;
	return (
		<Column mt={10} mb={3} style={{fontSize: 12, gap: 1}}>
			<Row mb={3} style={{fontWeight: "bold", alignItems: "center"}}>
				<Text>Buildings</Text>
				<InfoButton ml={5} text={`
					Per-building 3D mesh counts (Buildings Set1 §8), aggregated across all mounted buildings.
					* Buildings: how many met the "enabled, non-prototype Mod_Building" filter (3D mode only).
					* Meshes: how many THREE.Mesh are actually attached to the scene (opaque wall/floor/ceiling + transparent window-glass).
					* Rooms / Floors: the resolved §3.7 plan totals — these reflect DATA/plan-resolution, independent of mesh mount success.
					* Geom keys: the number of distinct shared BufferGeometries (§6.4).
					Interpretation — "0 Meshes but N>0 Rooms" ⇒ geometry/material production failed (not Data/plan resolution). "0 Rooms" ⇒ the plan itself resolved to nothing. "No buildings" ⇒ the modifier/filter never matched (see §6.1).
				`.AsMultiline(0)}/>
			</Row>
			{buildingCount == 0 && <Row style={{color: "gray", fontSize: 12}}>No building meshes (3D mode with an enabled non-prototype Mod_Building required).</Row>}
			{buildingCount > 0 &&
			<>
				<Row style={{alignItems: "baseline"}}>
					<Text style={{width: 100}}>Buildings:</Text>
					<Text style={{...monoAndPre, fontWeight: "bold"}}>{buildingCount}</Text>
				</Row>
				<Row style={{alignItems: "baseline"}}>
					<Text style={{width: 100}}>Meshes:</Text>
					<Text style={{...monoAndPre, fontWeight: "bold"}}>{meshCount.toLocaleString()}</Text>
					<Text ml={6} style={{color: "rgba(255,255,255,0.6)"}}>({roofCount.toLocaleString()} roof)</Text>
					{meshCount == 0 && roomCount > 0 && <Text ml={6} style={{color: "#ffb070", fontWeight: "bold"}}>⚠ 0 meshes but {roomCount} rooms</Text>}
				</Row>
				<Row style={{alignItems: "baseline"}}>
					<Text style={{width: 100}}>Rooms:</Text>
					<Text style={{...monoAndPre, fontWeight: "bold"}}>{roomCount.toLocaleString()}</Text>
					<Text ml={6} style={{color: "rgba(255,255,255,0.6)"}}>({floorCount.toLocaleString()} floors, {geometryKeyCount.toLocaleString()} geom keys)</Text>
				</Row>
				{vals.length == 1 && (()=>{
					const v = vals[0];
					return (
						<Row style={{alignItems: "baseline"}}>
							<Text style={{width: 100}}>Per-building:</Text>
							<Text style={{...monoAndPre, color: "rgba(255,255,255,0.7)"}}>
								{`${v.meshCount} meshes / ${v.roomCount} rooms / ${v.geometryKeyCount} geom`}
							</Text>
						</Row>
					);
				})()}
			</>}
		</Column>
	);
});